"use client";

import Link from "@/infrastructure/router/Link";
import { useMemo, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	GlassIconButton,
	GlassTabPill,
} from "@/components/design-system/ArtistDesignSystem";
import {
	type CreateComposerDraft,
	initialCreateDraft,
	MakeCreateTab,
} from "./MakeCreateTab";
import { MakeIPTab } from "./MakeIPTab";
import { MakeUploadTab } from "./MakeUploadTab";
import {
	defaultMakeModel,
	type IPContentTab,
	MAKE_MODELS,
	MAKE_TAB_LABELS,
	type MakeModelDescriptor,
	type MakeTab,
} from "./makeCatalog";

export function MakeStudio() {
	const [selectedTab, setSelectedTab] = useState<MakeTab>("create");
	const [ipContentTab, setIpContentTab] = useState<IPContentTab>("songs");
	const [createDraft, setCreateDraft] = useState<CreateComposerDraft>(
		initialCreateDraft(),
	);
	const [selectedModel, setSelectedModel] = useState<MakeModelDescriptor>(
		defaultMakeModel(),
	);
	const availableModels = useMemo(() => MAKE_MODELS, []);
	const auth = useArtistSession();
	const isAuthenticated = auth.phase === "user";

	function presentAuth() {
		if (typeof window !== "undefined") {
			window.location.assign("/auth");
		}
	}

	function clearCreateDraft() {
		setCreateDraft(initialCreateDraft());
	}

	const showCreateTrash =
		selectedTab === "create" &&
		(createDraft.lyrics.trim().length > 0 ||
			createDraft.styles.trim().length > 0 ||
			createDraft.negativePrompt.trim().length > 0 ||
			createDraft.title.trim().length > 0);

	function handleTrailingAction() {
		if (selectedTab === "create" && showCreateTrash) {
			clearCreateDraft();
			return;
		}
		if (selectedTab === "ip") {
			setIpContentTab((current) => (current === "songs" ? "notes" : "songs"));
		}
	}

	const trailingLabel =
		selectedTab === "create" && showCreateTrash
			? "清空草稿"
			: selectedTab === "ip"
				? "切换歌曲/笔记"
				: undefined;
	const trailingIcon: "trash" | "sliders" | null =
		selectedTab === "create" && showCreateTrash
			? "trash"
			: selectedTab === "ip"
				? "sliders"
				: null;

	return (
		<div className="make-studio" data-tab={selectedTab}>
			<header className="make-studio__chrome">
				<div className="make-studio__chrome-side make-studio__chrome-side--leading">
					<Link
						href="/discover"
						className="ds-icon-button make-studio__close"
						aria-label="关闭并返回首页"
					>
						<ArtistIcon name="close" size={22} strokeWidth={2.4} />
					</Link>
				</div>
				<div className="make-studio__chrome-center">
					<GlassTabPill
						tabs={["create", "ip", "upload"] as const}
						selected={selectedTab}
						onSelect={(value) => setSelectedTab(value as MakeTab)}
						label={(value) => MAKE_TAB_LABELS[value as MakeTab]}
						className="make-studio__tabs"
					/>
				</div>
				<div className="make-studio__chrome-side make-studio__chrome-side--trailing">
					{trailingIcon !== null && trailingLabel !== undefined ? (
						<GlassIconButton
							label={trailingLabel}
							kind={trailingIcon === "trash" ? "destructive" : "neutral"}
							type="button"
							onClick={handleTrailingAction}
						>
							<ArtistIcon name={trailingIcon} size={20} />
						</GlassIconButton>
					) : (
						<span
							className="make-studio__chrome-placeholder"
							aria-hidden="true"
						/>
					)}
				</div>
			</header>

			<main
				className="make-studio__content"
				id="make-studio-content"
				tabIndex={-1}
			>
				{selectedTab === "create" ? (
					<MakeCreateTab
						draft={createDraft}
						onDraftChange={setCreateDraft}
						selectedModel={selectedModel}
						availableModels={availableModels}
						onSelectModel={setSelectedModel}
						onClearDraft={clearCreateDraft}
					/>
				) : null}
				{selectedTab === "ip" ? (
					<MakeIPTab
						contentTab={ipContentTab}
						onContentTabChange={setIpContentTab}
					/>
				) : null}
				{selectedTab === "upload" ? (
					<MakeUploadTab
						isAuthenticated={isAuthenticated}
						onAuthenticationRequired={presentAuth}
					/>
				) : null}
			</main>
		</div>
	);
}
